import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowDown, RotateCw, ShieldCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inr = (value, decimals = 2) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

export default function Calculator() {
  const [status, setStatus] = useState("loading"); // loading | live | unavailable
  const [rate, setRate] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const fetchRate = useCallback(async (silent = false) => {
    if (!silent) setSpinning(true);
    try {
      const { data } = await axios.get(`${API}/rate/usdt-inr`, { timeout: 10000 });
      if (data && data.available && typeof data.rate === "number" && data.rate > 0) {
        setRate(data.rate);
        setUpdatedAt(data.updatedAt);
        setStatus("live");
      } else {
        setStatus("unavailable");
      }
    } catch (e) {
      setStatus("unavailable");
    } finally {
      setSpinning(false);
    }
  }, []);

  useEffect(() => {
    fetchRate(true);
  }, [fetchRate]);

  const updatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : null;

  return (
    <section id="exchange" data-testid="calculator-section" className="section-pad relative">
      <div
        aria-hidden="true"
        className="absolute right-0 top-1/4 h-[420px] w-[420px] rounded-full bg-emerald-500/[0.07] blur-[120px]"
      />
      <div className="container-x relative">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <span className="eyebrow justify-center" data-testid="calc-eyebrow">
              <span className="h-px w-8 bg-emerald-400/60" />
              Exchange Preview
              <span className="h-px w-8 bg-emerald-400/60" />
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              See how your <span className="text-gradient">exchange</span> works
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Create an order to enter your USDT amount and review the applicable
              INR calculation inside your account.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mx-auto mt-14 max-w-xl rounded-3xl bg-gradient-to-br from-emerald-400/40 via-white/10 to-teal-400/30 p-[1.5px] shadow-glow">
            <div className="rounded-[calc(1.5rem-1px)] bg-navy-900/95 p-6 backdrop-blur-xl sm:p-10">
              <div
                data-testid="calc-you-send"
                className="flex items-center justify-between rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    You Send
                  </p>
                  <p className="mt-1.5 font-display text-2xl font-bold text-white sm:text-3xl">
                    100 <span className="text-lg font-semibold text-slate-400">USDT</span>
                  </p>
                </div>
                <span className="grid h-11 w-11 place-items-center rounded-full bg-[#0B1120] font-mono text-sm font-bold text-emerald-300 ring-1 ring-emerald-400/30">
                  ₮
                </span>
              </div>

              <div className="relative my-2 flex justify-center">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-emerald-400/25 bg-navy-900 text-emerald-400">
                  <ArrowDown className="h-4 w-4" />
                </span>
              </div>

              <div
                data-testid="calc-rate"
                className="flex flex-col gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    Exchange Rate
                  </p>
                  {status === "live" ? (
                    <p
                      data-testid="calc-rate-value"
                      className="mt-1.5 whitespace-nowrap font-mono text-xl font-semibold text-emerald-300 sm:text-2xl"
                    >
                      ₹{inr(rate)} / USDT
                    </p>
                  ) : status === "loading" ? (
                    <p
                      data-testid="calc-rate-loading"
                      className="mt-1.5 animate-pulse font-mono text-xl font-semibold text-emerald-300/60 sm:text-2xl"
                    >
                      Fetching indicative rate…
                    </p>
                  ) : (
                    <p
                      data-testid="calc-rate-fallback"
                      className="mt-1.5 font-mono text-xl font-semibold text-emerald-300 sm:text-2xl"
                    >
                      ₹XX.XX / USDT
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  {status === "live" ? (
                    <span
                      data-testid="calc-rate-live-badge"
                      className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-center font-mono text-[11px] font-semibold uppercase tracking-wider text-emerald-300"
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping-soft absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      </span>
                      Live indicative rate
                      {updatedLabel && (
                        <span className="hidden font-normal normal-case text-slate-400 lg:inline">
                          · {updatedLabel} IST
                        </span>
                      )}
                    </span>
                  ) : (
                    <span
                      data-testid="calc-rate-order-badge"
                      className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-emerald-300"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      Rate shown at order creation
                    </span>
                  )}
                  <button
                    data-testid="calc-refresh-rate"
                    aria-label="Refresh indicative rate"
                    onClick={() => fetchRate(false)}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-emerald-400/60 hover:text-emerald-300"
                  >
                    <RotateCw className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>

              <div className="relative my-2 flex justify-center">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-emerald-400/25 bg-navy-900 text-emerald-400">
                  <ArrowDown className="h-4 w-4" />
                </span>
              </div>

              <div
                data-testid="calc-you-receive"
                className="flex items-center justify-between rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    You Receive
                  </p>
                  {status === "live" ? (
                    <p className="mt-1.5 font-display text-2xl font-bold text-white sm:text-3xl">
                      ₹{inr(100 * rate)}{" "}
                      <span className="text-lg font-semibold text-slate-400">INR</span>
                    </p>
                  ) : (
                    <p className="mt-1.5 font-display text-2xl font-bold text-white sm:text-3xl">
                      ₹X,XXX <span className="text-lg font-semibold text-slate-400">INR</span>
                    </p>
                  )}
                </div>
                <span className="grid h-11 w-11 place-items-center rounded-full bg-emerald-400 font-display text-base font-extrabold text-[#04121B]">
                  ₹
                </span>
              </div>

              <p
                data-testid="calc-disclaimer"
                className="mt-6 text-center text-xs leading-relaxed text-slate-500"
              >
                {status === "live"
                  ? "Indicative market rate for reference only — the applicable exchange rate for your order is confirmed when you create your order."
                  : "Example illustration only — not a live market rate. The applicable exchange rate is confirmed when you create your order."}
              </p>

              <Link
                data-testid="calc-cta"
                to="/app"
                onClick={() =>
                  sessionStorage.setItem("cdm-scroll", String(window.scrollY))
                }
                className="btn-primary mt-6 w-full py-4 text-base"
              >
                Create Exchange Order
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-6 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            Review your order details before submitting — nothing is processed
            without your confirmation.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
